import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CategoricalMatchByEnum, GenericDatasetComponent } from '../dataset/generic-dataset.component';
import { ColumnData } from '../../shared/models/column-data.model';
import { ObservationUnitsSearchResponse } from '../../../shared/dataset/model/observation-units-search-response.model';
import { VariableTypeEnum } from '../../../shared/ontology/variable-type.enum';
import { VariableDetails } from '../../../shared/ontology/model/variable-details';
import { ObservationVariable } from '../../../shared/model/observation-variable.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { StudyEditorContext } from '../../study-editor.context';
import { Principal } from '../../../shared';
import { StudyInstanceModel } from '../../../shared/dataset/model/study-instance.model';
import { DatasetModel } from '../../../shared/dataset/model/dataset.model';
import { GermplasmDetailsPopupService } from '../../../germplasm-details/germplasm-details-popup.service';
import { VIEW_GERMPLASM_DETAILS_PERMISSION } from '../../../shared/auth/permissions';
import { DatasetService } from '../../../shared/dataset/service/dataset.service';
import { ColumnDataService } from '../../shared/services/column-data.service';
import { ObservationUnitsSearchFilter, ObservationUnitsSearchRequest } from '../../../shared/dataset/model/observation-units-search-request.model';
import { DateFormatEnum, formatDate } from '../../../shared/util/date-utils';
import { DatasetTypeEnum } from '../../../shared/dataset/model/dataset-type.enum';
import { ObservationDetailsModalComponent } from '../../../entities/observation/observation-details-modal.component';
import { SubscriptionsManager } from '../../../shared/util/subscriptions-manager';
import { preventDefaultOnShiftKey } from '../../../field-management/utils';

@Component({
    selector: 'jhi-summary-dataset',
    templateUrl: './summary-dataset.component.html',
    styleUrls: ['./summary-dataset.component.scss']
})
export class SummaryDatasetComponent extends GenericDatasetComponent implements OnInit, OnChanges {

    public readonly CategoricalMatchByEnum = CategoricalMatchByEnum;
    hasViewGermplasmDetailsPermission = true;

    @Input() currentDataset: DatasetModel;
    @Input() isLoading = false;
    @Output() isLoadingChange = new EventEmitter<boolean>();

    environments: StudyInstanceModel[] = [];
    selectedEnvironment: StudyInstanceModel;
    hasNoData = false;

    subscriptionManager = new SubscriptionsManager();

    constructor(public studyEditorContext: StudyEditorContext,
                protected modalService: NgbModal,
                protected translateService: TranslateService,
                protected principal: Principal,
                public germplasmDetailsPopupService: GermplasmDetailsPopupService,
                protected datasetService: DatasetService) {
        super(studyEditorContext, modalService, translateService, principal);
    }

    private setLoading(loading: boolean): void {
        this.isLoading = loading;
        this.isLoadingChange.emit(this.isLoading);
    }


    ngOnChanges(changes: SimpleChanges): void {
        if (changes['currentDataset']) {
            // Check if the datasetId has changed to reinitialize the component
            if (changes.currentDataset?.currentValue === undefined || changes.currentDataset?.currentValue === null) {
                this.hasNoData = true;
                return;
            }
            if (changes.currentDataset?.currentValue?.datasetId !== changes.currentDataset?.previousValue?.datasetId) {
                this.initialize();
            }
        }
    }

    async ngOnInit() {
        this.initialize();
    }

    initialize() {

        this.setLoading(true);

        this.items = [];
        this.hasViewGermplasmDetailsPermission = this.principal.hasAnyAuthorityDirect(VIEW_GERMPLASM_DETAILS_PERMISSION);

        const allEnvironments = new StudyInstanceModel();
        allEnvironments.instanceId = -1;
        allEnvironments.locationName = this.translateService.instant('study-editor.all-environments');
        this.environments = [allEnvironments, ...this.currentDataset.instances];
        if (this.currentDataset.datasetTypeId === DatasetTypeEnum.SUMMARY_STATISTICS_DATA.valueOf()) {
            this.selectedEnvironment = this.environments[0];
        } else {
            this.selectedEnvironment = this.environments[1];
        }

        this.columnDataService = new ColumnDataService(ColumnDataService.SUMMARY_STATISTICS_KEY, String(this.currentDataset.datasetId));

        super.ngOnInit();
    }

    async loadDataset(datasetId: number): Promise<void> {
        // Not neccessary
    }

    async loadTable(): Promise<void> {
        this.setLoading(true);
        await this.loadTableHeader();
        this.loadTableData();
    }

    loadTableHeader(): Promise<void> {
        return this.datasetService.getObservationSetColumns(this.studyEditorContext.studyDetails.id, this.currentDataset.datasetId, this.isPendingView).toPromise().then((response) => {
            let columnsFromResponse = response.body as ColumnData[];
            columnsFromResponse.forEach(column => {
                column.visible = true;
                column.selected = true;
                column.columnSelectorVisible = true;
            });
            // Remove ENTRY DETAILS columns as they are not neeeded for obssrvation and sub-obs dataset.
            columnsFromResponse = columnsFromResponse.filter(column => VariableTypeEnum[column.variableType] !== VariableTypeEnum.ENTRY_DETAIL);
            this.displayedColumns = this.columnDataService.processColumns(columnsFromResponse, this.displayedColumns);
            this.displayedColumns = this.columnDataService.applyColumnsState();
        }, () => {
            this.hasNoData = true;
            this.setLoading(false);
        });
    }

    loadTableData(pageIndex: any = 1, pageSize: any = 50): void {
        const request: ObservationUnitsSearchRequest = new ObservationUnitsSearchRequest();
        request.instanceIds = this.getInstanceIds();
        request.draw = 'true';
        request.draftMode = this.isPendingView;
        request.filter = this.getFilter();
        request.visibleColumns = this.columnDataService.getVisibleColumns();
        request.datasetId = this.currentDataset.datasetId;

        const pagination: any = {
            page: pageIndex - 1,
            size: pageSize,
            sort: this.getSort(),
        };
        this.datasetService.getObservationUnitTable(this.studyEditorContext.studyDetails.id, this.currentDataset.datasetId, request, pagination).toPromise().then((response) => {
            this.numberOfItems = Number(response.headers.get('X-Total-Count'));
            this.numberOfFilteredItems = Number(response.headers.get('X-Filtered-Count'));
            this.items = response.body;
            this.hasNoData = this.items.length <= 0;
        }, () => {
            this.hasNoData = true;
            this.setLoading(false);
        }).finally(() => {
            this.setLoading(false);
        });

    }

    getFilter(): ObservationUnitsSearchFilter {
        return {
            byOutOfBound: null,
            byMissing: null,
            byOutOfSync: null,
            byOverwritten: null,
            variableId: null,
            variableHasValue: null,
            filteredValues: this.displayedColumns.reduce((map, column) => {
                column.isFiltered = false;

                if (this.isTextFilter(column)) {
                    return map;
                }

                if (column.possibleValues) {
                    column.possibleValues.forEach((value) => {
                        if (value.isSelectedInFilters) {
                            map[String(column.termId)] =  [...(map[String(column.termId)] || []), value.name];
                        }
                    });
                    if (!map[String(column.termId)] && column.query) {
                        map[String(column.termId)] = [column.query];
                    }
                } else if (column.query) {
                    if (column.dataType === 'Date') {
                        map[String(column.termId)] = [formatDate(column.query, DateFormatEnum.ISO_8601_BASIC)];
                    } else {
                        map[String(column.termId)] = [column.query];
                    }
                }

                if (map[String(column.termId)]) {
                    column.isFiltered = true;
                }
                return map;
            }, {} as Map<string, string[]>),
            filteredTextValues: this.displayedColumns.reduce((map, column) => {
                if (!this.isTextFilter(column)) {
                    return map;
                }
                if (column.query) {
                    map[String(column.termId)] = column.query;
                    column.isFiltered = true;
                }
                return map;
            }, {} as Map<string, string>),
            variableTypeMap: this.displayedColumns.reduce((map, column) => {
                map[String(column.termId)] = column?.variableType?.toString();
                return map;
            }, {} as Map<string, string>)
        };

    }

    isTextFilter(column: ColumnData): boolean {

        if (column.termId === this.STATIC_COLUMN_DESIGNATION) {
            return true;
        }

        // Factors like GID, GROUPGID, LINE_GID and TESTER_GID have 'Germplasm List' datatype but they should be treated as numeric
        if (column.termId === this.STATIC_COLUMN_GID || column.termId === this.STATIC_COLUMN_GROUPGID
            || column.termId === this.STATIC_COLUMN_LINE_GID || column.termId === this.STATIC_COLUMN_TESTER_GID) {
            return false;
        }

        if (column.dataTypeCode === 'C' || column.dataTypeCode === 'N'
            || column.dataTypeCode === 'D' || column.termId === this.STATIC_COLUMN_SAMPLES) {
            return false;
        }
        return true;
    }

    isColumnEditable(column: ColumnData): boolean {
        return false;
    }

    getCellValue(entry: ObservationUnitsSearchResponse, column: ColumnData): string {
        if (!entry?.variables || !column?.name) {
            return '';
        }

        if (column.termId === this.LOCATION_ID_TERM_ID) {
            return entry.variables[column.name].value;
        }

        const property = entry.variables[column.name];
        if (!property?.value && !property?.variableId) {
            return '';
        }

        return property.value;
    }

    concatAllColumns(): ColumnData[] {
        throw new Error('Method not implemented.');
    }

    getModel(column: ColumnData, entry: ObservationUnitsSearchResponse) {
        throw new Error('Method not implemented.');
    }

    addVariable(variable: VariableDetails, variableTypeId: VariableTypeEnum): Promise<void> {
        throw new Error('Method not implemented.');
    }

    removeVariables(variables: ObservationVariable[], variableTypeId: VariableTypeEnum): Promise<void> {
        throw new Error('Method not implemented.');
    }

    updateObservation(entry: ObservationUnitsSearchResponse, column: ColumnData): Promise<void> {
        throw new Error('Method not implemented.');
    }

    onEnvironmentChange($event: any) {
        this.selectedEnvironment = $event;
        this.scheduleReload();
    }

    getInstanceIds(): number[] {
        if (this.selectedEnvironment && this.selectedEnvironment.instanceId !== -1) {
            return [this.selectedEnvironment.instanceId];
        }
        return this.environments.map(env => env.instanceId).filter(id => id !== -1);
    }

    onColumnSelectorDeselectedAll() {
        this.displayedColumns.forEach(column => {
            if (column.visible) {
                column.selected = false;
            }
        });
        this.scheduleReload();
    }

    onColumnSelectorSelectedAll() {
        this.displayedColumns.forEach(column => {
            if (column.visible) {
                column.selected = true;
            }
        });
        this.scheduleReload();
    }

    onColumnSelectorReset() {
        this.onColumnSelectorSelectedAll();
    }

    openObservationDetails(observationUnitId: string) {
        const modalRef = this.modalService.open(ObservationDetailsModalComponent as Component, { size: 'xl', backdrop: 'static' });
        modalRef.componentInstance.datasetId = this.currentDataset.datasetId;
        modalRef.componentInstance.studyId = this.studyEditorContext.studyId;
        modalRef.componentInstance.observationUnitId = observationUnitId;
    }

    protected readonly preventDefaultOnShiftKey = preventDefaultOnShiftKey;
}
